import { accueilDuCabinet } from "@dust-tt/front/lib/compta/connexion/porte";
import { ConversationRouterLayout } from "@spa/app/layouts/ConversationRouterLayout";
import { withSuspense } from "@spa/app/routes/withSuspense";
import type { RouteObject } from "react-router-dom";
import { Navigate, useLocation, useParams } from "react-router-dom";

function RedirectWithSearchParams({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

// Redirect legacy conversation URLs (/w/:wId/assistant/:cId and /w/:wId/agent/:cId) to the current
// /w/:wId/conversation/:cId, preserving query params and hash. The deployed Cloudflare worker
// issues a real 301 for these paths, but the worker does not run under the Vite dev/preview server,
// so this client-side redirect guarantees the behavior in every environment.
function ConversationRedirect() {
  const { wId, cId } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={`/w/${wId}/conversation/${cId}${location.search}${location.hash}`}
      replace
    />
  );
}

const ConversationPage = withSuspense(
  () => import("@dust-tt/front/components/pages/conversation/ConversationPage"),
  "ConversationPage"
);

function ForYouRedirect() {
  const { wId } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={`/w/${wId}/conversation/new${location.search}${location.hash}`}
      replace
    />
  );
}

// compta (décision de Louka du 15/10) : dans Linéa, l'écran de conversation de
// Dust et son ancienne barre latérale n'ont pas leur place. L'index du cabinet
// et toute conversation renvoient au portefeuille. La bascule est posée au
// build : les apps assistantes, qui vivent dans le chat, ne sont pas touchées.
function VersLePortefeuille() {
  const { wId } = useParams();
  return <Navigate to={accueilDuCabinet(wId ?? "") ?? "/"} replace />;
}

const linea = accueilDuCabinet("w") !== null;

const conversationRoutesLinea: RouteObject[] = [
  { index: true, element: <VersLePortefeuille /> },
  { path: "for-you", element: <VersLePortefeuille /> },
  {
    path: "conversation",
    children: [
      { index: true, element: <VersLePortefeuille /> },
      { path: ":cId", element: <VersLePortefeuille /> },
    ],
  },
];

const conversationRoutesDust: RouteObject[] = [
  // Workspace index redirects to conversation/new
  {
    index: true,
    element: <RedirectWithSearchParams to="conversation/new" />,
  },
  // Old For You emails and bookmarks land on the homepage.
  {
    path: "for-you",
    element: <ForYouRedirect />,
  },
  {
    path: "conversation",
    element: <ConversationRouterLayout />,
    children: [{ path: ":cId", element: <ConversationPage /> }],
  },
];

export const conversationRoutes: RouteObject[] = linea
  ? conversationRoutesLinea
  : conversationRoutesDust;

// Legacy conversation URL redirects: /assistant/:cId and /agent/:cId -> /conversation/:cId.
export const conversationRedirectRoutes: RouteObject[] = [
  {
    path: "assistant/:cId",
    element: <ConversationRedirect />,
  },
  {
    path: "agent/:cId",
    element: <ConversationRedirect />,
  },
];
